import { Component, effect, ElementRef, inject } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';

import Chart from 'chart.js/auto';

import { RtudataStore } from '../rtudata.store';

@Component({
  imports: [MatCheckboxModule, MatFormFieldModule, MatIconModule],
  selector: 'norupe-greenhouse-data-overview',
  template: `
    @if (RtudataStore.selectedScanId()) {
      @let selectedSession =
        RtudataStore.entityMap()[RtudataStore.selectedScanId()!];
      <div class="border-b border-gray-200">
        <div class="shadow-default rounded-2xl pb-3">
          <div>
            <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">
              Greenhouse ID
              <span
                class="text-theme-sm mt-1 text-xs text-gray-500 dark:text-gray-400"
              >
                {{ selectedSession.greenhouse_id }}
              </span>
            </h3>
          </div>
          <div class="flex flex-col justify-center gap-5">
            <div
              class="overflow-hidden rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 shadow-sm sm:p-2"
            >
              <canvas id="StatusChart">{{ StatusChart }}</canvas>
            </div>
            <div
              class="overflow-hidden rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 shadow-sm sm:p-2"
            >
              <canvas id="TreeSpeciesChart">{{ TreeSpeciesChart }}</canvas>
            </div>
          </div>
          <dl class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
            @if (selectedSession.thermal_fgb_file) {
              <div
                class="overflow-hidden rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 shadow-sm sm:p-2"
              >
                <dt class="truncate text-sm font-medium text-gray-500">
                  Min. Temperatūra
                </dt>
                <dd
                  class="mt-1 text-3xl font-semibold tracking-tight text-gray-900"
                >
                  {{ selectedSession.thermal_results?.min_t }}°C
                </dd>
              </div>
              <div
                class="overflow-hidden rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 shadow-sm sm:p-2"
              >
                <dt class="truncate text-sm font-medium text-gray-500">
                  Max. Temperatūra
                </dt>
                <dd
                  class="mt-1 text-3xl font-semibold tracking-tight text-gray-900"
                >
                  {{ selectedSession.thermal_results?.max_t }}°C
                </dd>
              </div>
            }
            @if (selectedSession.ndvi_fgb_file) {
              <div
                class="overflow-hidden rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 shadow-sm sm:p-2"
              >
                <dt class="truncate text-sm font-medium text-gray-500">
                  Vidējais ndvi
                </dt>
                <dd
                  class="mt-1 text-3xl font-semibold tracking-tight text-gray-900"
                >
                  {{ selectedSession.ndvi_results?.average_ndvi }}
                </dd>
              </div>
            }
          </dl>
        </div>
      </div>
    }
  `,
})
export class GreenhouseDataOverviewComponent {
  readonly RtudataStore = inject(RtudataStore);
  private elementRef = inject(ElementRef);

  StatusChart: any;
  TreeSpeciesChart: any;

  constructor() {
    effect(() => {
      const selectedScan = this.RtudataStore.selectedScan();
      if (selectedScan) {
        this.RtudataStore.loadSession(selectedScan.id);
        setTimeout(() => {
          if (
            this.RtudataStore.entityMap()[this.RtudataStore.selectedScanId()!]
          ) {
            this.createStatusChart();
            this.createTreeSpeciesChart();
          }
        }, 100);
      }
    });
  }

  createStatusChart() {
    const sessionData =
      this.RtudataStore.entityMap()[this.RtudataStore.selectedScanId()!];

    if (this.StatusChart) {
      this.StatusChart.destroy();
    }

    const htmlRef = this.elementRef.nativeElement.querySelector(`#StatusChart`);

    const labels = this.RtudataStore.cellstatus()
      .sort((a, b) => a.id - b.id)
      .map((statu) => statu.value);

    const data = Object.values(sessionData.status_results?.status_counts ?? {});

    const backgroundColor = this.RtudataStore.cellstatus()

      .map((statu) => statu.style['stroke-color']);

    this.StatusChart = new Chart(htmlRef, {
      data: {
        datasets: [
          {
            backgroundColor,
            data,
          },
        ],
        labels,
      },
      options: {
        aspectRatio: 2.5,
        maintainAspectRatio: true,
        plugins: {
          legend: {
            align: 'end',
            display: true,
            position: 'right',
          },
          title: {
            align: 'start',
            display: true,
            font: {
              size: 16,
              weight: 'bold',
            },
            text: 'Šūnu statusa pārskats',
          },
        },
        responsive: true,
      },
      type: 'doughnut',
    });
  }

  createTreeSpeciesChart() {
    const sessionData =
      this.RtudataStore.entityMap()[this.RtudataStore.selectedScanId()!];

    if (this.TreeSpeciesChart) {
      this.TreeSpeciesChart.destroy();
    }

    const htmlRef =
      this.elementRef.nativeElement.querySelector(`#TreeSpeciesChart`);

    const labels = this.RtudataStore.treespecies()
      .sort((a, b) => a.id - b.id)
      .map((species) => species.value);

    const data = Object.values(
      sessionData.status_results?.tree_spiece_counts ?? {},
    );

    const backgroundColor = this.RtudataStore.treespecies()

      .map((species) => species.style['stroke-color']);

    this.TreeSpeciesChart = new Chart(htmlRef, {
      data: {
        datasets: [
          {
            backgroundColor,
            data,
          },
        ],
        labels,
      },
      options: {
        aspectRatio: 2.5,
        maintainAspectRatio: true,
        plugins: {
          legend: {
            align: 'end',
            display: true,
            position: 'right',
          },
          title: {
            align: 'start',
            display: true,
            font: {
              size: 16,
              weight: 'bold',
            },
            text: 'Koku sugu pārskats',
          },
        },
        responsive: true,
      },
      type: 'doughnut',
    });
  }
}
